import React from 'react';
import { CheckCircle, Sparkles, MessageSquare, ArrowRight } from 'lucide-react';

interface CompletionStepProps {
  data: any;
  onUpdate: (updates: any) => void;
  onNext: () => void;
}

export default function CompletionStep({ data, onUpdate, onNext }: CompletionStepProps) {
  const getSelectedPersonality = () => {
    if (data.selectedPersonality) {
      const personalityNames = {
        'drill-sergeant': 'Drill Sergeant',
        'fitness-coach': 'Fitness Coach',
        'best-friend': 'Best Friend',
        'mindful-guide': 'Mindful Guide',
        'teacher': 'Teacher',
        'wise-mentor': 'Wise Mentor'
      };
      return personalityNames[data.selectedPersonality as keyof typeof personalityNames] || 'Your Coach';
    }
    return 'Your Coach';
  };

  const getNextMessageTime = () => {
    if (data.morningTime) {
      const time = new Date(`2000-01-01T${data.morningTime}`);
      return time.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true });
    }
    return '8:00 AM';
  };

  return (
    <div className="text-center space-y-6">
      <div className="w-12 h-12 md:w-20 md:h-20 bg-gradient-to-br from-emerald-400 to-emerald-500 rounded-full flex items-center justify-center mx-auto">
        <CheckCircle className="h-10 w-10 text-white" />
      </div>
      
      <div>
        <h3 className="md:text-2xl font-bold text-gray-900 mb-4">
          You're All Set! 🎉
        </h3>
        <p className="md:text-base text-sm text-gray-600 leading-relaxed mb-6">
          Your AI accountability coach is ready to help you achieve your daily goals.
        </p>
      </div>

      {/* Summary */}
      <div className="bg-gray-50 rounded-xl p-6 text-left">
        <h4 className="font-semibold text-gray-900 mb-4 flex items-center">
          <Sparkles className="h-5 w-5 text-pink-400 mr-2" />
          Your Setup Summary
        </h4>
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-gray-600">Coach Personality:</span>
            <span className="font-medium text-gray-900">{getSelectedPersonality()}</span>
          </div>
          {data.intensityLevel && (
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Intensity Level:</span>
              <span className="font-medium text-gray-900">{data.intensityLevel}/5</span>
            </div>
          )}
          <div className="flex items-center justify-between">
            <span className="text-gray-600">Morning Message:</span>
            <span className="font-medium text-gray-900">{getNextMessageTime()}</span>
          </div>
          {data.communicationPreference && (
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Communication:</span>
              <span className="font-medium text-gray-900 capitalize">{data.communicationPreference}</span>
            </div>
          )}
        </div>
      </div>

      {/* Next Steps */}
      <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-6">
        <h4 className="font-semibold text-emerald-800 mb-4 flex items-center justify-center">
          <MessageSquare className="h-5 w-5 mr-2" />
          What Happens Next?
        </h4>
        <div className="space-y-3 text-sm text-emerald-700">
          <div className="flex items-start space-x-3">
            <div className="w-6 h-6 bg-emerald-200 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
              <span className="text-emerald-800 font-bold text-xs">1</span>
            </div>
            <span>Check WhatsApp for your first message from {getSelectedPersonality()}</span>
          </div>
          <div className="flex items-start space-x-3">
            <div className="w-6 h-6 bg-emerald-200 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
              <span className="text-emerald-800 font-bold text-xs">2</span>
            </div>
            <span>You'll receive your first morning prompt at {getNextMessageTime()}</span>
          </div>
          <div className="flex items-start space-x-3">
            <div className="w-6 h-6 bg-emerald-200 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
              <span className="text-emerald-800 font-bold text-xs">3</span>
            </div>
            <span>Your coach will check in with you each evening</span>
          </div>
        </div>
      </div>

      {/* <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
        <p className="text-blue-800 text-sm">
          💡 <strong>Pro tip:</strong> Save our WhatsApp number to your contacts for the best experience!
        </p>
      </div> */}

      {/* <button
        onClick={onNext}
        className="w-full bg-emerald-500 text-white px-6 py-4 rounded-xl hover:bg-emerald-600 transition-colors font-medium flex items-center justify-center space-x-2 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2"
      >
        <span>Go to Dashboard</span>
        <ArrowRight className="h-4 w-4" />
      </button> */}
    </div>
  );
}